import {useQuery} from "@apollo/client";

import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {
  GQL_METRICS_EXPLORER_DIMENSION_VALUES,
  MetricsExplorerDimensionValuesData,
} from "@/lib/queries/metricsExplorer.ts";

interface DimensionFilterSelectProps {
  /** Unique per series and dimension, so each label points at its own control. */
  fieldId: string;
  checkIds: number[];
  dataType: string;
  dimension: string;
  label: string;
  value: string[];
  start: Date;
  end: Date;
  onChange: (values: string[]) => void;
}

const toOption = (value: string): SelectOption => ({value, label: value});

/**
 * The values one dimension can be restricted to, for the window currently on screen.
 *
 * The list comes from the data rather than the schema, because only ClickHouse knows which
 * steps a script actually ran or which locations reported. Apollo caches by variables, so
 * two series filtering the same dimension over the same checks share one request.
 */
export const DimensionFilterSelect = ({
  fieldId,
  checkIds,
  dataType,
  dimension,
  label,
  value,
  start,
  end,
  onChange,
}: DimensionFilterSelectProps) => {
  const {data, loading, error} = useQuery<MetricsExplorerDimensionValuesData>(GQL_METRICS_EXPLORER_DIMENSION_VALUES, {
    skip: checkIds.length === 0 || dataType === "",
    variables: {
      check_ids: checkIds,
      data_type: dataType,
      dimension,
      start_date: start.toISOString(),
      end_date: end.toISOString(),
    },
  });

  // A value picked earlier need not occur in the window now on screen. Keeping it in the
  // list means moving the date range leaves the filter visible and removable, rather than
  // silently emptying the chart with no way to see why.
  const offered = data?.metricsExplorerDimensionValues.values ?? [];
  const options = Array.from(new Set([...offered, ...value])).map(toOption);

  return (
    <div>
      <FormLabel forId={fieldId}>{label}</FormLabel>
      <Select<SelectOption, true>
        inputId={fieldId}
        isMulti
        size="small"
        options={options}
        value={value.map(toOption)}
        onChange={(selected) => onChange([...(selected ?? [])].map((option) => option.value))}
        isLoading={loading}
        placeholder="All values"
      />
      {error && <FormSubtext error="Could not load the available values." />}
    </div>
  );
};
